/**
 * A row of side-panel tabs that never wraps. When the labels do not fit the panel's width (tablet, a badge), tabs
 * with an icon show only the icon; when that is not enough either, the last tabs move into a "More" menu and the
 * open tab always stays on the bar (tabFit.ts). Each tab has a tooltip with its full name and shortcut.
 */
import { ChevronDown } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useIntlShape, useT, type Translate } from "../i18n/I18nProvider";
import { fitTabs, type TabFit } from "./tabFit.ts";

export interface SideTab<T extends string> {
  id: T;
  label: string;
  /** Shown before the label; instead of it with `iconOnly`, or when the bar is compact. */
  icon?: ReactNode;
  iconOnly?: boolean;
  /** A count or "!" after the label (`title` explains it). `label`: what the badge means, appended to the tab's accessible name ("Setbacks" + ", rules changed"); the
   * visible text ("!") is then hidden from screen readers, so the tab's name never becomes "Setbacks!". */
  badge?: { text: string | number; title?: string; label?: string } | null;
  /** The full name when the label is short ("Bill of materials" for "BOM"); the tooltip shows it. */
  name?: string;
  /** The shortcut as shown on the key caps ("B", "⇧S"); the tooltip and the More menu show it. */
  shortcut?: string | null;
}

/** "Bill of materials (B)". */
export function tabTitle(tr: Translate, t: { label: string; name?: string; shortcut?: string | null }): string {
  const name = t.name ?? t.label;
  return t.shortcut ? tr("editor.sideTabs.title", { name, shortcut: t.shortcut }) : name;
}

export function SideTabs<T extends string>({ tabs, active, onSelect, label, className = "" }: { tabs: SideTab<T>[]; active: string; onSelect: (id: T) => void; label: string; className?: string }) {
  const tr = useT();
  const intl = useIntlShape();
  const barRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<TabFit>(() => ({ shown: tabs.map((_, i) => i), compact: false }));
  const shown = fit.shown;
  const [open, setOpen] = useState(false);
  const activeIndex = tabs.findIndex((t) => t.id === active);
  // re-measure when what a tab shows changes (a badge appears, a label is renamed)
  const signature = tabs.map((t) => `${t.id}:${t.label}:${t.iconOnly ? 1 : 0}:${t.badge?.text ?? ""}`).join("|");

  useLayoutEffect(() => {
    const bar = barRef.current;
    const measure = measureRef.current;
    if (!bar || !measure) return;
    const refit = () => {
      const widths = (Array.from(measure.children) as HTMLElement[]).map((b) => b.getBoundingClientRect().width);
      const n = tabs.length;
      const next = fitTabs(widths.slice(0, n), widths.slice(n, 2 * n), bar.clientWidth, widths[2 * n] ?? 0, activeIndex);
      setFit((prev) => (prev.compact === next.compact && prev.shown.length === next.shown.length && prev.shown.every((v, i) => v === next.shown[i]) ? prev : next));
    };
    refit();
    const ro = new ResizeObserver(refit);
    ro.observe(bar);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, activeIndex]);

  // the menu closes on a click elsewhere
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!barRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [open]);

  const hidden = tabs.filter((_, i) => !shown.includes(i));
  const iconOnly = (t: SideTab<T>, compact: boolean) => !!t.icon && (t.iconOnly || compact);
  /** The tab's accessible name when its badge has a meaning: "Setbacks, rules changed" (else its content). */
  const named = (t: SideTab<T>, name: string) => (t.badge?.label ? `${name}${t.badge.label}` : undefined);
  const badge = (t: SideTab<T>) =>
    t.badge ? (
      <span className="tab-count" title={t.badge.title} aria-hidden={t.badge.label ? true : undefined}>
        {t.badge.text}
      </span>
    ) : null;
  const content = (t: SideTab<T>, compact: boolean) => (
    <>
      {t.icon}
      {iconOnly(t, compact) ? <span className="sr-only">{t.label}</span> : <span className="side-tab-label">{t.label}</span>}
      {badge(t)}
    </>
  );
  return (
    <div className={`side-tabs ${className}`.trim()} role="tablist" aria-label={label} ref={barRef}>
      {shown.map((i) => {
        const t = tabs[i]!;
        return (
          <button key={t.id} className={`${t.id === active ? "on" : ""}${iconOnly(t, fit.compact) ? " icon-tab" : ""}`} onClick={() => onSelect(t.id)} role="tab" aria-selected={t.id === active} aria-label={named(t, t.label)} title={tabTitle(tr, t)}>
            {content(t, fit.compact)}
          </button>
        );
      })}
      {hidden.length > 0 && (
        <div className="side-tab-more">
          <button className={open ? "open" : ""} onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} title={tr("editor.sideTabs.moreTitle", { tabs: intl.formatList(hidden.map((t) => t.label), { type: "unit", style: "narrow" }) })}>
            {tr("editor.sideTabs.more")} <ChevronDown size={14} />
            {hidden.some((t) => t.badge) && <span className="side-tab-dot" aria-hidden />}
          </button>
          {open && (
            <div
              className="side-tab-menu"
              role="menu"
              onKeyDown={(e) => {
                if (e.key !== "Escape") return;
                e.stopPropagation(); // Esc closes the menu only, not the editor's selection
                setOpen(false);
              }}
            >
              {hidden.map((t) => (
                <button
                  key={t.id}
                  role="menuitem"
                  aria-label={named(t, t.name ?? t.label)}
                  title={tabTitle(tr, t)}
                  onClick={() => {
                    onSelect(t.id);
                    setOpen(false);
                  }}
                >
                  {t.icon}
                  <span className="side-tab-label">{t.name ?? t.label}</span>
                  {badge(t)}
                  {t.shortcut && <kbd className="side-tab-hint">{t.shortcut}</kbd>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* natural widths of every tab (with its label, then compact) and of More, measured off screen */}
      <div className="side-tabs-measure" aria-hidden ref={measureRef}>
        {[false, true].map((compact) =>
          tabs.map((t) => (
            <button key={`${compact}${t.id}`} tabIndex={-1} className={iconOnly(t, compact) ? "icon-tab" : ""}>
              {content(t, compact)}
            </button>
          )),
        )}
        <button tabIndex={-1}>
          {tr("editor.sideTabs.more")} <ChevronDown size={14} />
        </button>
      </div>
    </div>
  );
}
